import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import { convertDate, convertHours } from "../utils/dateConverter";
import MultilineTextField from "../shared/MultilineTextField";

const MAX_NOTE_LENGTH = 2048;
const PREVIEW_LINE_HEIGHT_EM = 1.43;
const PREVIEW_MAX_LINES = 5;

export function formatNoteCreatorPrefix(createdBy) {
  const name = typeof createdBy === "string" ? createdBy.trim() : "";
  return name ? `${name} — ` : "";
}

function ExpandableNotePreview({ noteId, value, expanded, onExpand }) {
  const textRef = useRef(null);
  const [isExpandable, setIsExpandable] = useState(false);

  const displayValue =
    value !== undefined && value !== null && String(value) !== ""
      ? String(value)
      : "-";

  useEffect(() => {
    const element = textRef.current;
    if (!element || expanded) {
      setIsExpandable(false);
      return;
    }

    const checkOverflow = () => {
      if (!element) {
        return;
      }

      setIsExpandable(element.scrollHeight > element.clientHeight + 1);
    };

    checkOverflow();

    if (typeof window !== "undefined") {
      window.requestAnimationFrame(checkOverflow);
    }
  }, [displayValue, expanded]);

  return (
    <Box mt={0.5}>
      <Typography
        ref={textRef}
        variant="body2"
        style={{
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          tabSize: 4,
          lineHeight: `${PREVIEW_LINE_HEIGHT_EM}em`,
          overflow: "hidden",
          maxHeight: expanded
            ? "none"
            : `${PREVIEW_LINE_HEIGHT_EM * PREVIEW_MAX_LINES}em`,
        }}
      >
        {displayValue}
      </Typography>

      {!expanded && isExpandable && (
        <Button
          size="small"
          color="primary"
          onClick={() => onExpand(noteId)}
          style={{
            marginTop: 4,
            padding: 0,
            minWidth: "auto",
            textTransform: "none",
          }}
        >
          Więcej...
        </Button>
      )}
    </Box>
  );
}

export default function CalibrationNotesDialog({
  open,
  onClose,
  title = "Notatki",
  notes = [],
  loading = false,
  error = null,
  editingNoteId = null,
  editingText = "",
  onEditingTextChange,
  saving = false,
  deletingId = null,
  newNoteText = "",
  onNewNoteTextChange,
  adding = false,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onDeleteNote,
  onStartAdd,
  onCancelAdd,
  onSaveNew,
  emptyText = "Brak notatek.",
}) {
  const [expandedNoteIds, setExpandedNoteIds] = useState({});

  useEffect(() => {
    if (!open) {
      setExpandedNoteIds({});
    }
  }, [open]);

  const handleExpandNote = (noteId) => {
    setExpandedNoteIds((prev) => ({
      ...prev,
      [noteId]: true,
    }));
  };

  const handleClose = useCallback(
    (event, reason) => {
      const isInputMode = adding || editingNoteId !== null;

      if (reason === "backdropClick" && isInputMode) {
        return;
      }

      onClose();
    },
    [adding, editingNoteId, onClose],
  );

  const isEditingTextTooLong = editingText.length > MAX_NOTE_LENGTH;
  const isNewTextTooLong = newNoteText.length > MAX_NOTE_LENGTH;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ style: { minHeight: "70vh" } }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers style={{ minHeight: 420 }}>
        {loading && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography color="error" style={{ marginBottom: 8 }}>
            {error}
          </Typography>
        )}

        {!loading && notes.length === 0 && <Typography>{emptyText}</Typography>}

        {!loading &&
          notes.map((note) => (
            <Box
              key={note.id}
              mb={2}
              p={1}
              border={1}
              borderRadius={4}
              borderColor="#ccc"
            >
              <Box display="flex" justifyContent="space-between">
                <Typography variant="subtitle2">
                  {formatNoteCreatorPrefix(note.createdBy)}
                  {convertDate(note.createdAt)} {convertHours(note.createdAt)}
                </Typography>
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => onStartEdit(note)}
                    disabled={saving || deletingId === note.id}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => onDeleteNote(note.id)}
                    disabled={saving || deletingId === note.id}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {editingNoteId === note.id ? (
                <Box mt={1}>
                  <MultilineTextField
                    rows={8}
                    rowsMax={16}
                    variant="outlined"
                    value={editingText}
                    onValueChange={onEditingTextChange}
                    inputProps={{ maxLength: MAX_NOTE_LENGTH }}
                    helperText={`${editingText.length}/${MAX_NOTE_LENGTH}`}
                    error={isEditingTextTooLong}
                  />
                  <Box mt={1} display="flex" justifyContent="flex-end">
                    <Button onClick={onCancelEdit} disabled={saving}>
                      Anuluj
                    </Button>
                    <Button
                      onClick={onSaveEdit}
                      color="primary"
                      disabled={
                        saving || !editingText.trim() || isEditingTextTooLong
                      }
                    >
                      Zapisz
                    </Button>
                  </Box>
                </Box>
              ) : (
                <ExpandableNotePreview
                  noteId={note.id}
                  value={note.text}
                  expanded={expandedNoteIds[note.id] === true}
                  onExpand={handleExpandNote}
                />
              )}
            </Box>
          ))}

        <Box mt={2}>
          {!adding ? (
            <Button onClick={onStartAdd} color="primary" disabled={saving}>
              Dodaj notatkę
            </Button>
          ) : (
            <Box>
              <MultilineTextField
                rows={8}
                rowsMax={16}
                variant="outlined"
                value={newNoteText}
                onValueChange={onNewNoteTextChange}
                placeholder="Treść notatki"
                inputProps={{ maxLength: MAX_NOTE_LENGTH }}
                helperText={`${newNoteText.length}/${MAX_NOTE_LENGTH}`}
                error={isNewTextTooLong}
              />
              <Box mt={1} display="flex" justifyContent="flex-end">
                <Button onClick={onCancelAdd} disabled={saving}>
                  Anuluj
                </Button>
                <Button
                  onClick={onSaveNew}
                  color="primary"
                  disabled={saving || !newNoteText.trim() || isNewTextTooLong}
                >
                  Zapisz
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Zamknij
        </Button>
      </DialogActions>
    </Dialog>
  );
}
